import dayjs, { Dayjs } from 'dayjs'
import dayjsIsToday from 'dayjs/plugin/isToday'
import dayjsTimezone from 'dayjs/plugin/timezone'
import dayjsUtc from 'dayjs/plugin/utc'
import dayjsBusinessDays from 'dayjs-business-days2'
import isNil from 'lodash.isnil'
import { CalendarEventMomentFragment, DelayType, TimeZone } from 'types/graphql'

import { SecondsInDay } from 'src/constants'

dayjs.extend(dayjsUtc)
dayjs.extend(dayjsTimezone)
dayjs.extend(dayjsIsToday)
dayjs.extend(dayjsBusinessDays)

export enum DateFormats {
  DateWithDayOfWeek = 'dddd MMMM D',
  FullDateWithDayOfWeek = 'dddd MMMM D, YYYY',
  FullDateWithDayOfWeekAndTime = 'dddd MMMM D, YYYY, h:mma',
  ISO8601Standard = 'YYYY-MM-DD',
  Month = 'MMMM',
  MonthDay = 'M-DD',
  MonthDayFullYear = 'M-DD-YYYY',
  MonthDayYear = 'M-DD-YY',
  MonthDayFullYearWithSlash = 'M/D/YYYY',
  MonthDayYearWithSlash = 'M/DD/YY',
  MonthNameDateNoTime = 'MMMM D, YYYY',
  MonthNameDateTime = 'MMMM D, YYYY, h:mma',
  NumericalDateTime = 'MM-DD-YY [at] h:mma',
  NumericalDateTimeConcise = 'MM-DD-YY, h:mma',
  NumericalDateTimeWithSeconds = 'MM-DD-YY, h:mma:ss',
  Time = 'h:mma',
  TimeWithAMPMUppercase = 'h:mm A',
  TimeWithAMPMLowercase = 'h:mm a',
}

/**
 * Formats a timestamp for display in the **browser's local timezone**. Same contract as
 * `api/src/utils/date.ts` `formatDate`: `dayjs(date).format(...)` with no `.utc()`.
 * For org-specific or UTC/semantic-date behavior, use the helpers documented there
 * (`formatDateInTimeZone`, `formatUTCDate`, `formatSemanticDate`, etc.).
 */
export const formatDate = (date: Date | string | Dayjs, dateFormat: DateFormats) => {
  return dayjs(date).format(dateFormat)
}

const isCallableDayjs = (value: unknown): value is Dayjs =>
  dayjs.isDayjs(value) && typeof (value as Dayjs).isValid === 'function'

/** Rehydrates Date picker values after React state / form snapshot round-trips. */
export const coerceFormFieldDateValue = (value: unknown): Dayjs | null => {
  if (value == null || value === '') {
    return null
  }

  if (isCallableDayjs(value)) {
    return value.isValid() ? value : null
  }

  if (typeof value === 'string' || value instanceof Date || typeof value === 'number') {
    const parsed = dayjs(value)
    return parsed.isValid() ? parsed : null
  }

  if (typeof value === 'object' && value !== null && '$d' in value) {
    const parsed = dayjs((value as { $d: string | Date | number }).$d)
    return parsed.isValid() ? parsed : null
  }

  return null
}

/** Semantic date string for form snapshots and API payloads (`@db.Date`). */
export const toFormFieldDateOnlyString = (value: unknown): string | null => {
  const parsedDate = coerceFormFieldDateValue(value)
  return parsedDate ? parsedDate.format(DateFormats.ISO8601Standard) : null
}

export const formatDateForTable = (
  date?: Date | string | null,
  dateFormat: DateFormats = DateFormats.MonthNameDateNoTime
) => {
  if (!date) {
    return '-'
  }

  return formatDate(date, dateFormat)
}

// this should be used for dates that are stored as semantic dates, i.e. db.Date in the database
export const formatSemanticDate = (
  date?: Date | string | null,
  dateFormat: DateFormats = DateFormats.MonthNameDateNoTime
) => {
  if (!date) {
    return '-'
  }

  // Handle semantic dates (from @db.Date) by parsing only the date portion
  // to avoid timezone conversion issues
  const dateString = typeof date === 'string' ? date : date.toISOString()
  const [year, month, day] = dateString.split('T')?.[0]?.split('-') ?? []

  // Create date using explicit year, month, day to avoid timezone parsing issues
  const semanticDate = dayjs()
    .year(parseInt(year ?? ''))
    .month(parseInt(month ?? '') - 1)
    .date(parseInt(day ?? ''))
  return semanticDate.format(dateFormat)
}

export const formatMoment = (
  moment: CalendarEventMomentFragment | null | undefined,
  dateFormat: DateFormats,
  timeZone: TimeZone | null | undefined,
  opts?: { dateOnly?: boolean }
): string => {
  if (moment?.__typename === 'CalendarEventMomentDate') {
    return formatDate(moment.date, dateFormat)
  }

  if (moment?.__typename === 'CalendarEventMomentDateTime') {
    if (opts?.dateOnly) {
      return formatDateInTimeZone(moment.startTime, timeZone, dateFormat)
    }
    return formatTimeRange(moment.startTime, moment.endTime, {
      showDates: true,
      timeZone,
    })
  }

  return ''
}

export const getDayjsInTimeZone = (
  date: Dayjs | Date | string,
  timeZone: TimeZone | null | undefined
) => dayjs(date).tz(timeZone ? TimeZoneMap[timeZone] : undefined)

const getTimeZoneSuffix = (timeZone: TimeZone | null | undefined) => {
  const clientTimeZone = dayjs.tz.guess()
  if (timeZone && clientTimeZone !== TimeZoneMap[timeZone]) {
    return ` (${TimeZoneAbbreviationMap[timeZone]})`
  }
  return ''
}

export const formatDateInTimeZone = (
  date: Date | string,
  timeZone: TimeZone | null | undefined,
  dateFormat: DateFormats
) => {
  const formattedDate = getDayjsInTimeZone(date, timeZone).format(dateFormat)
  const timeZoneSuffix = getTimeZoneSuffix(timeZone)
  return `${formattedDate}${timeZoneSuffix}`
}

export const formatTimeRange = (
  startTime: Dayjs | Date | string,
  endTime: Dayjs | Date | string,
  args?: {
    showDates?: boolean
    timeZone?: TimeZone | null
  }
) => {
  const startTimeInTz = getDayjsInTimeZone(startTime, args?.timeZone)
  const endTimeInTz = getDayjsInTimeZone(endTime, args?.timeZone)
  const isSameDay = startTimeInTz.format('YYYY-MM-DD') === endTimeInTz.format('YYYY-MM-DD')
  const currentYear = dayjs().year()
  const showYear =
    startTimeInTz.year() !== currentYear ||
    endTimeInTz.year() !== currentYear ||
    startTimeInTz.year() !== endTimeInTz.year()
  const showStartMeridian = !!(
    args?.showDates ||
    startTimeInTz.hour() < 12 != endTimeInTz.hour() < 12 ||
    !isSameDay
  )
  const showStartMinutes = !!(args?.showDates || startTimeInTz.minute())
  const showEndMinutes = !!(args?.showDates || endTimeInTz.minute())

  const minuteFormatString = ':mm'
  const meridianFormatString = 'a'

  const dateFormat = showYear ? DateFormats.FullDateWithDayOfWeek : DateFormats.DateWithDayOfWeek
  const startDate = args?.showDates ? `${startTimeInTz.format(dateFormat)} @ ` : ''
  const startTimeDisplay = startTimeInTz.format(
    `h${showStartMinutes ? minuteFormatString : ''}${showStartMeridian ? meridianFormatString : ''}`
  )
  const endDate =
    args?.showDates && !isSameDay
      ? `${getDayjsInTimeZone(endTimeInTz, args?.timeZone).format(dateFormat)} @ `
      : ''
  const endTimeDisplay = endTimeInTz.format(`h${showEndMinutes ? minuteFormatString : ''}a`)
  const timeZoneSuffix = getTimeZoneSuffix(args?.timeZone)
  return `${startDate}${startTimeDisplay} - ${endDate}${endTimeDisplay}${timeZoneSuffix}`
}

/**
 * Antd time pickers will preserve the time zone of any pre-existing field value when a user changes the time.
 * However, if the user sets a time in a blank field, the antd time pickers will always instantiate the value using the client time zone, which may differ from the org time zone.
 * This function should be used when writing a form field value to the backend; it enforces that the internal time value sent to the backend corresponds to the org time zone.
 *
 * IF YOU'RE USING THIS ON NEW CODE YOU'RE DOING SOMETHING WRONG.
 * Do not store abstract time of day as an instant/datetime, instead use a number of minutes since midnight or some equivalent.
 *
 * @param date
 * @deprecated
 */
export const getTimeOnlyDayjsInTimeZone = (
  date: Dayjs | null | undefined,
  timeZone: TimeZone | undefined
) => {
  if (!date || !timeZone) {
    return date
  }

  // NOTE! THIS IS STILL BUGGY!
  // The evaluted time will not match what the user entered if one time zone respects daylight savings while the other does not.
  // To fix, we ultimately need to stop storing abstract time as Datetime
  const timeZoneStrippedDateString = date.format('YYYY-MM-DD HH:mm:ss')
  return dayjs.tz(timeZoneStrippedDateString, TimeZoneMap[timeZone])
}

// is the date at least one day in the past?
export const isBeforeToday = (date: Dayjs | Date | string) => {
  return dayjs(date).isBefore(dayjs(), 'day')
}

export const isToday = (date: Dayjs | Date | string) => {
  return dayjs(date).isToday()
}

export const isAfterToday = (date: Dayjs | Date | string) => {
  return dayjs(date).isAfter(dayjs(), 'day')
}

export const isTodayOrBefore = (date: Dayjs | Date | string) => {
  return isBeforeToday(date) || isToday(date)
}

export const isTodayOrAfter = (date: Dayjs | Date | string) => {
  return isAfterToday(date) || isToday(date)
}

export const getDueDate = (daysToComplete?: number | null): Date | undefined => {
  if (daysToComplete == null) {
    return undefined
  }
  return dayjs(new Date()).businessDaysAdd(daysToComplete).toDate()
}

export const removeSeconds = (date: Date | string) => {
  return dayjs(date).second(0).millisecond(0).toDate()
}

/**
 * Treats the date input as a semantic date (only takes the YYYY-MM-DD information, ignoring time)
 */
export const convertDateToTimeZone = <TDate extends Dayjs | null | undefined>(
  date: TDate,
  timeZone: TimeZone | null | undefined
): Dayjs | TDate => {
  if (!timeZone || isNil(date)) {
    return date
  }
  const dateOnlyString = date.format(DateFormats.ISO8601Standard)
  return convertInstantToTimeZone(dateOnlyString, timeZone)
}

export const beginningOfDayInTimeZone = (date: Dayjs, timeZone: TimeZone): Dayjs => {
  return convertDateToTimeZone(date, timeZone).startOf('day')
}

export const endOfDayInTimeZone = (date: Dayjs, timeZone: TimeZone): Dayjs => {
  return beginningOfDayInTimeZone(date, timeZone).endOf('day')
}

// convertDateToTimeZone exists because AntdDatePicker does not allow the timezone to be
// selected:  It is needed to translate a selected date to the desired timezone.
//
// For example, suppose the following the local browser time zone is EST and the
// user wants to generate a report using Central time: If '01-01-25, 12:00am' is
// chosen in the date picker, then we are given the form value date of
// '2025-01-01T05:00:00.272Z' (which is the chosen time in the local offset, the
// start of the day in EST), and this function would then convert it to the
// desired '2025-01-01T06:00:00.272Z' (which is one hour later, the start of the
// day in central).
export const convertInstantToTimeZone = (
  date: Dayjs | Date | string,
  timeZone: TimeZone | null | undefined
): Dayjs => {
  if (!timeZone) {
    return dayjs(date)
  }

  // Convert the date to Day.js object in UTC
  const dayjsDateUTC = dayjs(date).utc()

  // Get the offset in hours for the target time zone
  const targetOffset = dayjs().tz(TimeZoneMap[timeZone]).utcOffset()
  const localOffset = dayjs().utcOffset()

  // Calculate the difference in minutes and adjust the date
  const adjustedDate = dayjsDateUTC
    .add(localOffset - targetOffset, 'minute')
    .tz(TimeZoneMap[timeZone])

  return adjustedDate
}

/**
 * Two-letter abbreviations for the org-TZ enum. DST-agnostic on purpose —
 * `'EDT'`/`'EST'` would imply the configured wall-clock shifts across
 * DST boundaries, which it does not (the BE re-anchors wall-clock via
 * `combineDateAndTimeInTimeZone`).
 */
export const TimeZoneAbbreviationMap: Record<TimeZone, string> = {
  Pacific: 'PT',
  Mountain: 'MT',
  MountainStandard: 'MST',
  Central: 'CT',
  Eastern: 'ET',
}

export const TimeZoneDisplayMap: Record<TimeZone, string> = {
  Pacific: 'Pacific Time',
  Mountain: 'Mountain Time',
  MountainStandard: 'Mountain Standard Time',
  Central: 'Central Time',
  Eastern: 'Eastern Time',
}

export const TimeZoneMap: Record<TimeZone, string> = {
  Pacific: 'America/Los_Angeles',
  Mountain: 'America/Denver',
  MountainStandard: 'America/Phoenix',
  Central: 'America/Chicago',
  Eastern: 'America/New_York',
}

export const Months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

/**
 * Gets the number of days in a month ignoring leap years
 * @param month 1-indexed month
 * @returns 1-indexed day
 */
export const getNumberOfDaysForMonth = (month: number) => {
  let maxNumberOfDays = 31
  if (month === 4 || month === 6 || month === 9 || month === 11) {
    // April, June, September, November
    maxNumberOfDays = 30
  } else if (month === 2) {
    // February
    maxNumberOfDays = 28 // Specifically not allowing configuring leap year 29, since this day config could apply to any year
  }
  return maxNumberOfDays
}

export const getGraphQLDateString = <T extends string | Date | Dayjs | null | undefined>(
  value: T
) => {
  return !isNil(value) ? dayjs(value).format(DateFormats.ISO8601Standard) : value
}

const convertMilliseconds = (milliseconds: number): Durations => {
  const seconds = Math.floor(milliseconds / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  const remainingMinutes = minutes % 60
  const remainingHours = hours % 24

  return {
    days,
    hours: remainingHours,
    minutes: remainingMinutes,
  }
}

interface Durations {
  minutes: number
  hours: number
  days: number
}
export const formatDuration = (milliseconds: number) => {
  const { minutes, hours, days } = convertMilliseconds(milliseconds)

  if (!minutes && !hours && !days) {
    return '0'
  }

  const parts = []

  if (days > 0) {
    parts.push(`${days}d`)
  }
  if (hours > 0) {
    parts.push(`${hours}h`)
  }
  if (minutes > 0 && days < 1) {
    parts.push(`${minutes}m`)
  }

  return parts.join(', ')
}

const weekendDays = [0, 6]

const getIsBeforeCutoff = (cutoffTime: Date | string, timeZone: TimeZone | null | undefined) => {
  const now = dayjs()
  const nowInTimeZone = getDayjsInTimeZone(now, timeZone)
  const cutoff = getDayjsInTimeZone(cutoffTime, timeZone)

  if (nowInTimeZone.hour() > cutoff.hour()) {
    return false
  }
  if (nowInTimeZone.hour() < cutoff.hour()) {
    return true
  }
  if (nowInTimeZone.minute() > cutoff.minute()) {
    return false
  }
  return true
}

// Used in web/src/components/form/DatePicker.tsx
export const getFirstAllowableDay = (
  cutoffTime: Date | string | null | undefined,
  timeZone: TimeZone | null | undefined,
  disableWeekends: boolean
) => {
  if (!cutoffTime) {
    return undefined
  }

  const isBeforeCutoff = getIsBeforeCutoff(cutoffTime, timeZone)
  let firstAllowableDay = getDayjsInTimeZone(dayjs(), timeZone)

  if (!disableWeekends) {
    const offset = isBeforeCutoff ? 1 : 2
    return firstAllowableDay.add(offset, 'day')
  }

  firstAllowableDay = firstAllowableDay.add(1, 'day')

  while (weekendDays.includes(firstAllowableDay.day())) {
    firstAllowableDay = firstAllowableDay.add(1, 'day')
  }
  const offset = isBeforeCutoff ? 0 : 1
  firstAllowableDay = firstAllowableDay.add(offset, 'day')
  while (weekendDays.includes(firstAllowableDay.day())) {
    firstAllowableDay = firstAllowableDay.add(1, 'day')
  }
  firstAllowableDay = firstAllowableDay
    .set('hour', 0)
    .set('minute', 0)
    .set('second', 0)
    .set('millisecond', 0)
  return firstAllowableDay
}

export const secondsToDays = (seconds: number | null | undefined) => {
  if (!seconds) {
    return 0
  }

  // round to the nearest day, but at least 1 day
  return Math.max(1, Math.round(seconds / SecondsInDay))
}

type RelativeDateTime = {
  n: number
  unit: dayjs.OpUnitType
}

const getRelativeDateTime = (dt: Dayjs): RelativeDateTime | null => {
  const now = dayjs()

  if (dt.isAfter(now)) {
    return null
  }

  const diffSeconds = now.diff(dt, 'second')
  if (diffSeconds < 60) {
    return { n: diffSeconds, unit: 'second' }
  }

  const diffMinutes = now.diff(dt, 'minute')
  if (diffMinutes < 60) {
    return { n: diffMinutes, unit: 'minute' }
  }

  const diffHours = now.diff(dt, 'hour')
  if (diffHours < 24) {
    return { n: diffHours, unit: 'hour' }
  }

  const diffDays = now.diff(dt, 'day')
  if (diffDays < 7) {
    return { n: diffDays, unit: 'day' }
  }

  const diffWeeks = now.diff(dt, 'week')
  if (diffWeeks < 4) {
    return { n: diffWeeks, unit: 'week' }
  }

  const diffMonths = now.diff(dt, 'month')
  if (diffMonths === 0) {
    return { n: diffWeeks, unit: 'week' }
  }
  if (diffMonths < 12) {
    return { n: diffMonths, unit: 'month' }
  }

  return { n: now.diff(dt, 'year'), unit: 'year' }
}

export const getRelativeDateTimeString = (date: Date | string | null | undefined) => {
  if (!date) {
    return ''
  }

  const dt = dayjs(date)
  const result = getRelativeDateTime(dt)
  if (!result) {
    return ''
  }

  const { n, unit } = result
  return `${n} ${unit}${n === 1 ? '' : 's'} ago`
}

// Converts a semantic date (YYYY-MM-DD) to the local date in the client's time zone
export const getLocalizedDateFromSemanticDate = (
  date: Date | string | null | undefined
): Dayjs | null => {
  if (!date) {
    return null
  }

  // Parse as UTC to get correct year/month/day, then create local date with same values
  const utcDate = dayjs.utc(date)
  return dayjs().year(utcDate.year()).month(utcDate.month()).date(utcDate.date()).startOf('day')
}

/**
 * Converts a Dayjs value to a UTC midnight ISO string (e.g. "2025-06-15T00:00:00.000Z"),
 * preserving only the date portion. Use this when sending a semantic date (@db.Date)
 * to the backend as a timestamp — it ensures the value is pinned to UTC midnight
 * so no local-timezone offset can shift the date.
 */
export const semanticDateToUTCTimestamp = (date: Dayjs): string => {
  return `${date.format(DateFormats.ISO8601Standard)}T00:00:00.000Z`
}

const FORM_DATE_RE = /^\d{4}-\d{2}-\d{2}$/
// Matches every bare-time variant: `HH:mm`, `HH:mm:ss(.SSS)?`, and the
// trailing-`Z` form graphql-scalars emits. `dayjs.utc()` returns
// Invalid Date for any of these, so this pass-through prevents
// "Invalid Date" from leaking into form inputs.
const FORM_TIME_RE = /^\d{2}:\d{2}(:\d{2}(\.\d+)?)?Z?$/

/**
 * Converts a `@db.Date` scalar (UTC ISO or `Date`) into the `YYYY-MM-DD`
 * string forms typically hold. Reads calendar fields off `dayjs.utc(...)`
 * to dodge local-TZ offset shifts.
 */
export const toDateOnlyString = (value: Date | string | null | undefined): string | null => {
  if (value == null) {
    return null
  }
  if (typeof value === 'string' && FORM_DATE_RE.test(value)) {
    return value
  }
  return dayjs.utc(value).format('YYYY-MM-DD')
}

/**
 * Converts a `@db.Time` scalar (bare time string or `Date` at
 * `1970-01-01` UTC) into the `HH:mm` form string. Pairs with
 * {@link toDateOnlyString}.
 */
export const toHourMinuteString = (value: Date | string | null | undefined): string | null => {
  if (value == null) {
    return null
  }
  if (typeof value === 'string' && FORM_TIME_RE.test(value)) {
    return value.slice(0, 5)
  }
  return dayjs.utc(value).format('HH:mm')
}

/** Formats `'HH:mm:ss'` (or `'HH:mm'`) as a 12-hour clock with AM/PM (e.g. `'8:00 AM'`). */
export const formatTimeOfDay = (time: string): string => {
  const parsed = dayjs(`1970-01-01T${time.length === 5 ? `${time}:00` : time}`)
  if (!parsed.isValid()) {
    return time
  }
  return parsed.format('h:mm A')
}

/** Maps a GraphQL `DelayType` enum to the corresponding dayjs manipulation unit. */
export const delayTypeToDayjsUnit = (unit: DelayType): dayjs.ManipulateType => {
  switch (unit) {
    case 'Minute':
      return 'minute'
    case 'Hour':
      return 'hour'
    case 'Day':
      return 'day'
    case 'Week':
      return 'week'
    case 'Month':
      return 'month'
    case 'Year':
      return 'year'
    default:
      return 'day'
  }
}

export const getSemanticDate = (date: Dayjs | null | undefined): Date | null => {
  if (!date) {
    return null
  }

  return new Date(date.year(), date.month(), date.date())
}
